/**
 * Note: Before developing this content,
 * you must first define the theme token values the component.
 */
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken } from './theme';
import { randomStr } from '../../Shared';
export const componentName = 'Spinning';
export const classPrefix = `ui-Spinning`;
/**
 * The classes here are the independent classes of the react-ui component.
 * It is mainly isolated from the classes of the mui component.
 * Avoid style conflicts when mui and react-ui are used simultaneously in project applications.
 *
 * On the way to develop react-ui components based on mui,
 * you must use this react-ui classes instead of mui classes.
 * You can control the built-in classes of the mui component through the classes prop of mui component.
 */
export const spinningClasses = {
    // It is recommended that the root classname of the react-ui component be added with a random string.
    // This is mainly to avoid classname naming conflicts in the application
    root: `${classPrefix}-root-${randomStr()}`,
    text: `${classPrefix}-text`,
    spinWrapper: `${classPrefix}-spin-wrapper`,
    spinWrapperClipping: `${classPrefix}-spin-wrapper-clipping`,
    spinWrapperClipped: `${classPrefix}-spin-wrapper-clipped`,
    spikes: `${classPrefix}-spikes`,
    spikesSpin: `${classPrefix}-spikes-spin`,
    spikesItem: `${classPrefix}-spikes-item`,
    nestedWrapper: `${classPrefix}-nested-wrapper`,
    nestedContentWrapper: `${classPrefix}-nested-content-wrapper`,
};
/**
 * All style css codes of react-ui components are centrally managed in this method through classes.
 * Through this method, the light and dark themes of the components can be controlled.
 * You don't need to care about the implementation of theme switching.
 * You only need to define the specified style value based on the content of the token.
 */
export function getStyle(theme, opts) {
    // getDynamicThemeToken It can dynamically control the semantic design token of the current component.
    // It is a dynamtic theme token to control two themes (light and dark) of this component.
    const dynamicToken = getDynamicThemeToken(theme);
    // generateDynamicThemeToken will return two contents, one is token data, and the other is tk method.
    // The token includes the light and dark theme style values defined by this component,
    // and it will automatically switch the token content of light or dark according to the outer ThemeProvider.
    // The role of tk is to process custom theme configuration,
    // and override the application custom token to the default light or dark token.
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const spikesItemWidth = tk(token.spikesItemSize[opts.size]);
    const spikesItemHeight = spikesItemWidth * 2;
    return {
        [`&.${spinningClasses.nestedWrapper}`]: {
            position: 'relative',
            [`>.${spinningClasses.nestedContentWrapper}`]: {
                position: 'relative',
                transition: 'opacity 0.2s',
                '&::after': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    zIndex: 10,
                    background: tk(token.underlay),
                    content: '""',
                    pointerEvents: 'none',
                    userSelect: 'none',
                },
                '&-blur': {
                    opacity: 0.5,
                    pointerEvents: 'none',
                    userSelect: 'none',
                },
            },
            [`>.${spinningClasses.spinWrapper}`]: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                [`&.${spinningClasses.spinWrapperClipping}.${spinningClasses.spinWrapperClipped}`]: {
                    padding: 8,
                    width: 'auto',
                    height: 'auto',
                    transform: 'translate(-50%,-50%)',
                },
            },
        },
        [`&.${spinningClasses.spinWrapper}`]: {
            display: 'inline-flex',
            position: 'relative',
            textAlign: 'center',
            [`.${spinningClasses.text}`]: {
                marginTop: tk(token.labelTop[opts.size]),
                color: tk(token.color.neutral),
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.fontSize[opts.size]),
            },
            [`.${spinningClasses.spikes}`]: {
                position: 'relative',
                display: 'inline-block',
                width: tk(token.height[opts.size]),
                height: tk(token.height[opts.size]),
                textAlign: 'center',
                [`&-spin`]: {
                    animationName: 'antSpinRotate',
                    animationDuration: '1.5s',
                    animationIterationCount: 'infinite',
                    animationTimingFunction: 'linear',
                    '@keyframes antSpinRotate': {
                        from: { transform: 'rotate(0)' },
                        to: { transform: 'rotate(360deg)' },
                    },
                },
                [`&-item`]: {
                    position: 'absolute',
                    top: `calc(50% - ${spikesItemHeight}px - ${tk(token.gap[opts.size])}px)`,
                    left: `calc(50% - ${spikesItemWidth / 2}px)`,
                    background: tk(token.color.spikesItem),
                    width: spikesItemWidth,
                    height: spikesItemHeight,
                    borderRadius: 33,
                    transformOrigin: `50% ${tk(token.gap[opts.size]) + spikesItemHeight}px`,
                    '&:nth-of-type(1)': {
                        opacity: '40%',
                    },
                    '&:nth-of-type(2)': {
                        opacity: '35%',
                        transform: 'rotate(45deg)',
                    },
                    '&:nth-of-type(3)': {
                        opacity: '30%',
                        transform: 'rotate(90deg)',
                    },
                    '&:nth-of-type(4)': {
                        opacity: '25%',
                        transform: 'rotate(135deg)',
                    },
                    '&:nth-of-type(5)': {
                        opacity: '20%',
                        transform: 'rotate(180deg)',
                    },
                    '&:nth-of-type(6)': {
                        opacity: '15%',
                        transform: 'rotate(225deg)',
                    },
                    '&:nth-of-type(7)': {
                        opacity: '10%',
                        transform: 'rotate(270deg)',
                    },
                    '&:nth-of-type(8)': {
                        opacity: '8%',
                        transform: 'rotate(315deg)',
                    },
                },
            },
        },
    };
}
